import { useCallback, useEffect, useRef, useState } from "react";
import ISav from "../../../../models/sav";
import ICompany from "../../../../models/company";
import { isLogged } from "../../../../Stores/AuthStore";
import { authenticatedApi, publicApi } from "../../../../Api/api";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

type ReturnType = {
  sav: ISav | null;
  setSav: (sav: ISav | null) => void;
  loading: boolean;
  error: Error | null;
  load: (savId: number) => Promise<ISav | null>;
  refetch: () => Promise<ISav | null>;
  reparator: ICompany | null;
  reparatorRefetching: boolean;
  refetchingSav: boolean;
  refetchReparator: () => Promise<ICompany | null>;
};

export function useCurrentSavNotNull(): ReturnType & { sav: ISav } {
  return useCurrentSav(true);
}

export default function useCurrentSav(mandatorySav?: true): ReturnType & { sav: ISav };
export default function useCurrentSav(mandatorySav?: false): ReturnType;
export default function useCurrentSav(mandatorySav = false): ReturnType {
  const {
    sav,
    loading,
    error,
    setSav,
    setLoading,
    setError,
  } = useCurrentSavStorage();
  const userIsLogged = isLogged();

  // Nouveaux états pour le réparateur et les refetchs
  const [reparator, setReparator] = useState<ICompany | null>(null);
  const [reparatorRefetching, setReparatorRefetching] = useState(false);
  const [refetchingSav, setRefetchingSav] = useState(false);
  const lastReparatorUuid = useRef<string | undefined>(undefined);

  // Fonction pour charger le réparateur
  const fetchReparator = useCallback(async (reparatorUuid: string): Promise<ICompany | null> => {
    setReparatorRefetching(true);
    try {
      const api = userIsLogged ? authenticatedApi : publicApi;
      const response = await api.get(`/companies/${reparatorUuid}`);
      setReparator(response.data as ICompany);
      return response.data as ICompany;
    } catch (error) {
      setReparator(null);
      return null;
    } finally {
      setReparatorRefetching(false);
    }
  }, [userIsLogged]);

  // Effet pour charger le réparateur à l'initialisation ou si l'id change
  useEffect(() => {
    if (sav?.reparatorUuid) {
      if (lastReparatorUuid.current !== sav.reparatorUuid) {
        lastReparatorUuid.current = sav.reparatorUuid;
        fetchReparator(sav.reparatorUuid);
      }
    } else {
      setReparator(null);
      lastReparatorUuid.current = undefined;
    }
  }, [sav?.reparatorUuid, fetchReparator]);

  // Fonction pour refetch le réparateur manuellement
  const refetchReparator = useCallback(async (): Promise<ICompany | null> => {
    if (sav?.reparatorUuid) {
      return await fetchReparator(sav.reparatorUuid);
    }
    return null;
  }, [sav?.reparatorUuid, fetchReparator]);

  // Fonction pour charger le SAV (et le réparateur si besoin)
  const fetchData = useCallback(async (savId: number, isRefetch = false): Promise<ISav | null> => {
    if (!isRefetch) {
      setLoading(true);
    } else {
      setRefetchingSav(true);
    }
    const api = userIsLogged ? authenticatedApi : publicApi;
    try {
      const response = await api.get(`/sav/${savId}`);
      setSav(response.data as ISav);
      // Si le réparateur a changé, on le recharge
      if (response.data.reparatorUuid && response.data.reparatorUuid !== lastReparatorUuid.current) {
        lastReparatorUuid.current = response.data.reparatorUuid;
        await fetchReparator(response.data.reparatorUuid);
      }
      return response.data as ISav;
    } catch (error: Error | any) {
      setError(error);
    } finally {
      setLoading(false);
      setRefetchingSav(false);
    }
    return null;
  }, [setLoading, userIsLogged, setSav, setError, fetchReparator]);

  if (mandatorySav && !sav) {
    throw new Error("No current sav");
  }

  return {
    sav,
    setSav,
    loading,
    error,
    load: fetchData,
    refetch: async () => {
      if (sav) {
        return await fetchData(sav.id, true);
      }
      return null;
    },
    reparator,
    reparatorRefetching,
    refetchingSav,
    refetchReparator,
  };
}

const useCurrentSavStorage = create<{
  sav: ISav | null;
  setSav: (sav: ISav | null) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;

  error: Error | null;
  setError: (error: Error) => void;

}>()(
  devtools(
    (set) => ({
      sav: null,
      loading: false,
      setSav: (sav: ISav | null) => set({ sav }),

      setLoading: (loading: boolean) => set({ loading }),

      error: null,
      setError: (error: Error | null) => set({ error }),

    })
  )
);